import React from "react";
import { motion } from "framer-motion";
import { FaArrowRight, FaStar, FaAward } from "react-icons/fa";
import { Link } from "react-router-dom";

import excellenceImg from "../assets/excellence.jpeg";

const Welcome = () => {
  return (
    <section className="relative w-full bg-[#f7f9fc] overflow-hidden py-20">

      {/* ================= DECORATIVE BACKGROUND ================= */}

      <div className="absolute top-0 left-0 w-72 h-72 bg-[#d4af37]/5 rounded-full blur-3xl"></div>

      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#07294d]/5 rounded-full blur-3xl"></div>

      <div className="absolute top-24 right-[45%] w-3 h-3 bg-[#d4af37]/40 rounded-full"></div>
      <div className="absolute top-32 right-[43%] w-2 h-2 bg-[#07294d]/30 rounded-full"></div>

      {/* ================= MAIN ================= */}

      <div className="relative max-w-[1450px] mx-auto px-6 grid lg:grid-cols-2 gap-16 xl:gap-24 items-center">

        {/* ================= LEFT CONTENT ================= */}

        <motion.div
          initial={{ opacity: 0, x: -70 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >

          {/* SMALL TAG */}

          <div className="flex items-center gap-4 mb-8">

            <div className="w-16 h-[4px] bg-[#d4af37] rounded-full"></div>

            <p className="uppercase tracking-[8px] text-[#d4af37] font-bold text-sm">
              Welcome To Cambridge
            </p>

          </div>


          {/* HEADING */}

          <h2 className="text-5xl md:text-6xl xl:text-7xl font-black text-[#07294d] leading-[1.05] mb-10">

            Welcome To

            <br />

            <span className="relative inline-block">

              Our School

              <div className="absolute -bottom-3 left-0 w-full h-4 bg-[#d4af37]/20 rounded-full -z-10"></div>

            </span>

          </h2>


          {/* TEXT */}

          <p className="text-gray-600 text-lg leading-9 mb-8">

            Cambridge International School Nakodar started in 2016 with 290
            students under the aegis of S.L. Angra Cambridge International
            Educational Society.

          </p>


          <p className="text-gray-600 text-lg leading-9 mb-8">

            The school is run in association with Learning Wings Education
            Systems, a global organization devoted to high-quality and
            leading-edge education practices.

          </p>


          <p className="text-gray-600 text-lg leading-9 mb-12">

            Learning Wings works closely with schools to raise student
            achievement while helping every child discover self-potential,
            creativity, confidence, and excellence.

          </p>


          {/* BUTTONS */}

          <div className="flex flex-wrap gap-5">

            <Link to="/faqs">

              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-[#07294d] text-white rounded-2xl font-bold flex items-center gap-3 shadow-lg hover:bg-[#0b3d6d] transition duration-300"
              >

                Read More

                <FaArrowRight />

              </motion.button>

            </Link>


            <Link to="/about">

              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 border-2 border-[#d4af37] text-[#07294d] rounded-2xl font-bold hover:bg-[#d4af37] transition duration-300"
              >

                Explore Campus

              </motion.button>

            </Link>

          </div>

        </motion.div>


        {/* ================= RIGHT IMAGE AREA ================= */}

        <motion.div
          initial={{ opacity: 0, x: 70 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9 }}
          viewport={{ once: true }}
          className="relative flex justify-center items-center py-8"
        >

          {/* BIG GOLD DECORATIVE CIRCLE */}

          <div className="absolute -top-6 -right-8 w-36 h-36 rounded-full border-[18px] border-[#d4af37]/15"></div>


          {/* BIG BLUE DECORATIVE CIRCLE */}

          <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-[#07294d]/5"></div>


          {/* DOT PATTERN */}

          <div className="absolute right-0 top-1/2 grid grid-cols-3 gap-3 opacity-50">

            {[...Array(9)].map((_, i) => (
              <span
                key={i}
                className="w-2.5 h-2.5 rounded-full bg-[#d4af37]"
              ></span>
            ))}

          </div>


          {/* ================= IMAGE FRAME ================= */}

          <div className="relative w-full max-w-[520px]">

            {/* GOLD OFFSET FRAME */}

            <div className="absolute inset-0 translate-x-5 translate-y-5 rounded-[45px] bg-[#d4af37]/30"></div>


            {/* BLUE OFFSET FRAME */}

            <div className="absolute inset-0 -translate-x-4 -translate-y-4 rounded-[45px] border-2 border-[#07294d]/10"></div>


            {/* IMAGE CONTAINER */}

            <div className="relative bg-white rounded-[45px] p-3 shadow-[0_30px_80px_rgba(0,0,0,0.18)]">

              <div className="relative rounded-[37px] overflow-hidden bg-[#f3f5f8]">

                <img
                  src={excellenceImg}
                  alt="Cambridge International School - Excellence Celebration"
                  className="w-full h-[620px] object-contain transition duration-700 hover:scale-[1.02]"
                />

                {/* IMAGE BOTTOM SHADOW */}

                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>

              </div>

            </div>


            {/* ================= TOP FLOATING CARD ================= */}

            <motion.div
              animate={{
                y: [0, -8, 0],
              }}
              transition={{
                duration: 3.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -top-8 -left-8 bg-white rounded-2xl px-5 py-4 shadow-2xl border border-gray-100"
            >

              <div className="flex items-center gap-3">

                <div className="w-11 h-11 rounded-xl bg-[#07294d] text-white flex items-center justify-center">
                  <FaAward />
                </div>

                <div>

                  <p className="text-2xl font-black text-[#07294d]">
                    2016
                  </p>

                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                    Established
                  </p>

                </div>

              </div>

            </motion.div>


            {/* ================= BOTTOM FLOATING CARD ================= */}

            <motion.div
              animate={{
                y: [0, 8, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -bottom-8 -right-8 bg-white rounded-3xl px-6 py-5 shadow-2xl border border-gray-100"
            >

              <div className="flex items-center gap-3">

                <div className="flex -space-x-2">

                  <div className="w-10 h-10 rounded-full bg-[#07294d] border-2 border-white flex items-center justify-center text-white">
                    <FaStar className="text-sm" />
                  </div>

                  <div className="w-10 h-10 rounded-full bg-[#d4af37] border-2 border-white flex items-center justify-center text-white">
                    <FaAward className="text-sm" />
                  </div>

                </div>

                <div>

                  <h3 className="text-xl font-black text-[#07294d]">
                    Excellence
                  </h3>

                  <p className="text-xs text-gray-500 font-semibold">
                    Celebrating Achievement
                  </p>

                </div>

              </div>

            </motion.div>


            {/* ================= SMALL GOLD BADGE ================= */}

            <motion.div
              animate={{
                rotate: [0, 5, 0, -5, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
              }}
              className="absolute bottom-24 -left-10 w-16 h-16 rounded-full bg-[#d4af37] flex items-center justify-center shadow-xl border-4 border-white"
            >

              <FaStar className="text-white text-xl" />

            </motion.div>

          </div>

        </motion.div>

      </div>


      {/* ================= BOTTOM MINI STATS ================= */}

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="relative max-w-[1100px] mx-auto px-6 mt-20"
      >

        <div className="bg-white rounded-[28px] shadow-xl border border-gray-100 p-6 md:p-8">

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">

            <div className="border-r border-gray-100 last:border-0">

              <h3 className="text-3xl font-black text-[#07294d]">
                2016
              </h3>

              <p className="text-sm text-gray-500 mt-1">
                Established
              </p>

            </div>


            <div className="border-r border-gray-100 last:border-0">

              <h3 className="text-3xl font-black text-[#07294d]">
                10+
              </h3>

              <p className="text-sm text-gray-500 mt-1">
                Years of Excellence
              </p>

            </div>


            <div className="border-r border-gray-100 last:border-0">

              <h3 className="text-3xl font-black text-[#07294d]">
               1000+
              </h3>

              <p className="text-sm text-gray-500 mt-1">
                Students Started
              </p>

            </div>


            <div>

              <h3 className="text-3xl font-black text-[#07294d]">
                1
              </h3>

              <p className="text-sm text-gray-500 mt-1">
                Vision for Excellence
              </p>

            </div>

          </div>

        </div>

      </motion.div>

    </section>
  );
};

export default Welcome;